import { useCallback, useMemo, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { tc } from '../../lib/trackingContext';
import { useDesigner } from '../useDesigner';
import { useSubscribe } from '../useSubscribe';
import { useDesignerSubscribe } from '../useSubscribe/useDesignerSubscribe';
import { useEmailVerification } from '../useEmailVerification';
import { useIsCloudFrontViewerCountryUS } from '../useIsCloudfrontViewerCountryUS';
import { useBackInStockNotificationMutation } from './useBackInStockNotificationMutation';

export const useBackInStockNotificationForm = (variant: { id: string }, sku?: string | null) => {
  const isCloudFrontViewerCountryUS = useIsCloudFrontViewerCountryUS();
  const { isValid: isEmailValid, error } = useEmailVerification();
  const [email, setEmail] = useState('');
  const { notify, data, loading } = useBackInStockNotificationMutation(sku, email);
  const [extraSubscriptionsChecked, setExtraSubscriptionsChecked] = useState(
    isCloudFrontViewerCountryUS ? true : false
  );
  const { isSubscribed: isSubscribedToDesigner, subscribeToDesigner } = useDesignerSubscribe();
  const { subscribe: subscribeToNewsletter } = useSubscribe();

  const { designerSlug } = useParams<{ designerSlug: string }>();
  const { designer } = useDesigner(designerSlug);

  const isSubscribed = useMemo(
    () => data?.backInStockNotificationMutation.success === true,
    [data?.backInStockNotificationMutation.success]
  );

  useEffect(() => {
    tc.track('PDP Back In Stock Notification Request Displayed', {
      addToScopeOnce: { variant },
      addToTrackingEvent: { label: variant.id, sku: sku || '' }
    });
  }, [variant, sku]);

  useEffect(() => {
    if (isSubscribed) {
      tc.track('PDP Back In Stock Notification Request Submitted', {
        addToScopeOnce: { variant },
        addToTrackingEvent: { label: variant.id, sku: sku || '' }
      });
    }
  }, [isSubscribed, variant, sku]);

  const handleClick = useCallback(() => {
    if (!isEmailValid(email)) return;

    notify()?.then(() => {
      if (!extraSubscriptionsChecked) return;

      if (isSubscribedToDesigner) return;

      subscribeToNewsletter({ email, label: 'productDetails' });

      if (!designer) return;

      subscribeToDesigner(email, designer);
    });
  }, [
    designer,
    email,
    extraSubscriptionsChecked,
    isEmailValid,
    isSubscribedToDesigner,
    notify,
    subscribeToDesigner,
    subscribeToNewsletter
  ]);

  return {
    isEmailValid,
    error,
    email,
    setEmail,
    handleClick,
    isSubscribed,
    loading,
    extraSubscriptionsChecked,
    setExtraSubscriptionsChecked
  };
};
